import { useEffect, useMemo, useState } from "react";
import { UserPlus, Loader2, Pencil, Users, AlertTriangle, UserX, UserCheck, Mail, MailX, Send, Filter, Trash2, ShieldAlert, Link as LinkIcon } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MatrizPermisos } from "@/components/rbac/MatrizPermisos";
import { ReasignarLoteUsuario } from "@/components/usuarios/ReasignarLoteUsuario";
import { TooltipButton } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { esActividadVulnerable } from "@/lib/actividad_vulnerable";
import { defaultAreasForRole } from "@/lib/rbac";
import { ROLES_PLATAFORMA_ASIGNABLES } from "@/lib/nav";
import { toast } from "sonner";

const FALLBACK_AREAS = [
  { id: "principal", label: "Principal" },
  { id: "originacion", label: "Originación de Crédito" },
  { id: "leads_campo", label: "Leads de campo" },
  { id: "cartera", label: "Administración de Cartera" },
  { id: "cobranza", label: "Cobranza" },
  { id: "clientes", label: "Clientes y Expedientes" },
  { id: "cumplimiento", label: "Cumplimiento PLD" },
  { id: "regulatorio", label: "Regulatorio" },
  { id: "administracion", label: "Administración" },
];

const EMPTY = { name: "", email: "", telefono: "", role: "", area: "Administración", puesto: "", areas_acceso: [], activo: true };

export default function UsuariosRoles() {
  const { user, perms, meta } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  // Cartera asignada (FASE 1): clientes de la entidad + selección por usuario.
  const [clientesAll, setClientesAll] = useState([]);
  const [carteraSel, setCarteraSel] = useState(new Set());
  const [carteraSearch, setCarteraSearch] = useState("");
  const [carteraSaving, setCarteraSaving] = useState(false);
  const [conCredito, setConCredito] = useState(new Set());
  // Conteo de clientes por usuario (originados + cartera) y diálogo de reasignación en lote.
  const [conteo, setConteo] = useState({});
  const [loteUser, setLoteUser] = useState(null);
  // feb-2026 iter 07-23j · Gestión honesta de usuarios (opciones B + D del veredicto de auditoría).
  //  - `filtroEstatus`: filtra el listado principal por Activos / Inactivos / Todos (las
  //    invitaciones pendientes viven en su propia tab, nunca aquí).
  //  - `confirmDesactivar`: diálogo de confirmación con conteo de cartera y aviso claro
  //    (los clientes no regresan solos al reactivar; el admin decide reasignar o no).
  //  - `confirmCancel`: diálogo para cancelar invitación (aclara que baja lógica, no borrado).
  //  - `busyRow`: id del usuario cuya acción está en vuelo (deshabilita botones).
  const [filtroEstatus, setFiltroEstatus] = useState("activos");
  const [confirmDesactivar, setConfirmDesactivar] = useState(null); // { row, cartera_info }
  const [confirmCancel, setConfirmCancel] = useState(null); // row (invitación pendiente)
  const [confirmEliminar, setConfirmEliminar] = useState(null); // { row, huella, elegible, cargando }
  const [busyRow, setBusyRow] = useState(null);

  const canManage = ["super_admin", "admin_entidad"].includes(user?.role);
  const isPlatform = user?.role === "super_admin";
  // ago-2026 — El catálogo de roles ya NO se decide aquí: lo manda el backend en
  // `GET /api/roles-disponibles`, filtrado por el RÉGIMEN de la entidad (mismo patrón que
  // `planes-disponibles`). En actividad vulnerable devuelve dos roles (Administrador de
  // Entidad + Oficial de Cumplimiento) por segregación de funciones LFPIORPI Art. 18, más
  // los roles heredados que la entidad YA tenga asignados, para poder reasignarlos.
  // El candado real vive en el backend: `POST/PUT /api/usuarios` responde 400 si el rol no
  // aplica al régimen. Esta lista solo evita ofrecer lo que se va a rechazar.
  const esAV = esActividadVulnerable(user?.giro);
  const [catRoles, setCatRoles] = useState(null);
  useEffect(() => {
    if (isPlatform) return;
    api.get("/roles-disponibles").then((r) => setCatRoles(r.data)).catch(() => {});
  }, [isPlatform]);
  const roles = catRoles
    ? [...(catRoles.roles || []), ...(catRoles.roles_en_uso || [])]
    // sep-2026 · En modo PLATAFORMA solo se ofrecen los roles de plataforma. Antes se
    // ofrecía el catálogo SOFOM completo, así que se podía invitar un "Cobranza" sin
    // entidad: un usuario que entra y no ve nada. El backend además lo rechaza
    // (`rol_cargador_listas.validar_asignacion`).
    : (meta?.roles || []).filter((r) => (isPlatform
        ? ROLES_PLATAFORMA_ASIGNABLES.includes(r.id)
        : r.id !== "super_admin"));
  const areasSugeridasDe = (rid) =>
    roles.find((r) => r.id === rid)?.areas_sugeridas || defaultAreasForRole(rid);
  const areasHr = catRoles?.areas_organigrama || meta?.areas || [];
  const menuAreas = perms?.menu_areas?.length ? perms.menu_areas : FALLBACK_AREAS;
  // ago-2026 — En actividad vulnerable el expediente del cliente tiene su área PROPIA
  // (`av_clientes`) y además reutiliza la compartida `clientes` para los endpoints de cliente
  // y Beneficiario Controlador. Las dos comparten etiqueta ("Clientes y Expedientes"), así que
  // el diálogo pintaba la misma casilla dos veces. Se muestra una sola: la propia del vertical,
  // y al marcarla se otorga también la compartida (`espejoAreas` de abajo).
  const AREA_ESPEJO = { av_clientes: "clientes" };
  const idsMenu = new Set(menuAreas.map((a) => a.id));
  const areasVisibles = menuAreas.filter(
    (a) => !Object.entries(AREA_ESPEJO).some(([propia, espejo]) => a.id === espejo && idsMenu.has(propia))
  );
  const espejoAreas = (ids) => {
    const out = new Set(ids || []);
    Object.entries(AREA_ESPEJO).forEach(([propia, espejo]) => {
      if (out.has(propia) && idsMenu.has(espejo)) out.add(espejo);
    });
    return [...out];
  };

  const load = () => {
    api.get("/usuarios").then((r) => setRows(r.data)).catch(() => {});
    if (user?.role === "admin_entidad") {
      api.get("/usuarios/clientes-conteo").then((r) => setConteo(r.data?.conteo || {})).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []);

  const conteoDe = (id) => conteo[id] || { originados: 0, cartera: 0 };
  const totalClientesDe = (id) => { const c = conteoDe(id); return (c.originados || 0) + (c.cartera || 0); };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onRole = (role) => {
    // Al elegir rol, pre-marca las áreas sugeridas por el backend (el admin puede ajustarlas).
    setForm((p) => ({ ...p, role, areas_acceso: areasSugeridasDe(role) }));
  };

  const toggleArea = (areaId) => {
    if (areaId === "principal") return; // siempre incluida
    setForm((p) => {
      const has = (p.areas_acceso || []).includes(areaId);
      const next = has ? p.areas_acceso.filter((a) => a !== areaId) : [...(p.areas_acceso || []), areaId];
      return { ...p, areas_acceso: next };
    });
  };

  const areaChecked = (areaId) => areaId === "principal" || (form.areas_acceso || []).includes(areaId);

  const openNew = () => {
    setEditing(null);
    // ago-2026 — Sin rol por omisión: el campo arranca VACÍO y el admin tiene que elegir.
    // Antes arrancaba en "lectura" y, como ese rol no existe en actividad vulnerable, el
    // selector se veía en blanco y se creaban usuarios de solo lectura sin querer.
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || "", email: row.email || "", telefono: row.telefono || "",
      role: row.role || "", area: row.area || "Administración", puesto: row.puesto || "",
      areas_acceso: row.areas_acceso?.length ? row.areas_acceso : areasSugeridasDe(row.role),
      activo: row.activo !== false,
    });
    setCarteraSearch("");
    setCarteraSel(new Set());
    if (!isPlatform) {
      api.get("/clientes").then((r) => setClientesAll(r.data || [])).catch(() => {});
      api.get(`/usuarios/${row.id}/cartera`).then((r) =>
        setCarteraSel(new Set((r.data?.clientes || []).map((c) => c.id)))).catch(() => {});
      api.get("/cartera/clientes-con-credito").then((r) => setConCredito(new Set(r.data?.ids || []))).catch(() => {});
    }
    setOpen(true);
  };

  const toggleCartera = (cid) => {
    setCarteraSel((prev) => {
      const next = new Set(prev);
      next.has(cid) ? next.delete(cid) : next.add(cid);
      return next;
    });
  };

  const saveCartera = async () => {
    if (!editing) return;
    setCarteraSaving(true);
    try {
      const { data } = await api.put(`/usuarios/${editing.id}/cartera`, { cliente_ids: [...carteraSel] });
      toast.success(`Cartera actualizada: ${data.asignados} cliente(s) asignado(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la cartera");
    } finally {
      setCarteraSaving(false);
    }
  };

  const carteraFiltrada = clientesAll.filter((c) => {
    if (!carteraSearch) return true;
    const s = carteraSearch.toLowerCase();
    return `${c.nombre || c.razon_social || ""} ${c.rfc || ""}`.toLowerCase().includes(s);
  });

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Captura nombre y correo"); return; }
    if (!form.role) { toast.error("Elige el rol del usuario: es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/usuarios/${editing.id}`, {
          name: form.name, telefono: form.telefono, role: form.role, area: form.area,
          puesto: form.puesto, areas_acceso: espejoAreas(form.areas_acceso), activo: form.activo,
        });
        toast.success("Usuario actualizado");
      } else {
        const { data } = await api.post("/usuarios", { ...form, areas_acceso: espejoAreas(form.areas_acceso) });
        toast.success(
          data.invitacion_enviada
            ? "Usuario invitado. Se le envió un correo para activar su cuenta."
            : "Usuario invitado. No se pudo enviar el correo de activación; reenvíalo más tarde."
        );
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (id) =>
    meta?.roles_labels?.[id] || (meta?.roles || []).find((r) => r.id === id)?.label || id;
  const areasLabel = (row) => {
    if (["super_admin", "admin_entidad"].includes(row.role)) return "Todas (admin)";
    const ids = row.areas_acceso?.length ? row.areas_acceso : defaultAreasForRole(row.role);
    return `${ids.length} área(s)`;
  };

  // Un usuario está en "invitación pendiente" cuando fue invitado pero nunca aceptó el correo
  // (email_verificado=false y estatus_activacion=pendiente). Al activar su cuenta, el propio
  // flujo `/activar?token=` cambia `email_verificado:true` y sale de este grupo.
  const esInvitacionPendiente = (r) =>
    r.invitado === true && r.email_verificado !== true;

  const rowsUsuarios = useMemo(
    () => rows.filter((r) => !esInvitacionPendiente(r)),
    [rows]
  );
  const rowsPendientes = useMemo(
    () => rows.filter((r) => esInvitacionPendiente(r)),
    [rows]
  );

  const rowsUsuariosFiltrados = useMemo(() => {
    if (filtroEstatus === "activos") return rowsUsuarios.filter((r) => r.activo !== false);
    if (filtroEstatus === "inactivos") return rowsUsuarios.filter((r) => r.activo === false);
    return rowsUsuarios;
  }, [rowsUsuarios, filtroEstatus]);

  // ---------- Acciones inline (backend endpoints honestos) ----------
  const abrirConfirmDesactivar = async (row) => {
    // Precarga el conteo de cartera para avisar al admin cuántos clientes tiene asignados.
    // Si el usuario no tiene rol operativo (p.ej. `lectura`), el conteo será 0 y el aviso
    // simplemente confirma que no hay cartera afectada.
    setBusyRow(row.id);
    try {
      const { data } = await api.get(`/usuarios/${row.id}/cartera-count`);
      setConfirmDesactivar({ row, cartera: data });
    } catch (e) {
      // Si falla el conteo, permitimos continuar sin él (fail-open, sólo pierdes la métrica).
      setConfirmDesactivar({ row, cartera: null, cartera_error: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyRow(null);
    }
  };

  const desactivar = async () => {
    if (!confirmDesactivar?.row) return;
    const row = confirmDesactivar.row;
    setBusyRow(row.id);
    try {
      await api.post(`/usuarios/${row.id}/desactivar`);
      toast.success(`Usuario ${row.name} desactivado. Pierde acceso inmediato; sus registros se conservan.`);
      setConfirmDesactivar(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desactivar el usuario");
    } finally {
      setBusyRow(null);
    }
  };

  const reactivar = async (row) => {
    setBusyRow(row.id);
    try {
      await api.post(`/usuarios/${row.id}/reactivar`);
      toast.success(`Usuario ${row.name} reactivado. Recupera su rol y áreas anteriores.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reactivar el usuario");
    } finally {
      setBusyRow(null);
    }
  };

  const reenviarInvitacion = async (row) => {
    setBusyRow(row.id);
    try {
      const { data } = await api.post(`/usuarios/${row.id}/reenviar-invitacion`);
      if (data?.invitacion_enviada) {
        toast.success(`Invitación reenviada a ${row.email}.`);
      } else {
        toast.warning(`Invitación registrada, pero el correo no salió (Brevo falló). Reintenta en 1 min.`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reenviar la invitación");
    } finally {
      setBusyRow(null);
    }
  };

  // Feb-2026 · fix P1 iter-101 · Copiar enlace de activación cuando no hay SMTP disponible.
  // Genera un token fresco vía `/usuarios/{id}/link-invitacion` y copia la URL al portapapeles.
  const copiarLinkInvitacion = async (row) => {
    setBusyRow(row.id);
    try {
      const { data } = await api.post(`/usuarios/${row.id}/link-invitacion`);
      const url = data?.url;
      if (!url) throw new Error("Sin URL");
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Enlace de activación copiado al portapapeles (válido ${data?.expira_en || "24 horas"}). Compártelo con ${row.email} por chat, WhatsApp o correo alterno.`);
      } catch (_clip) {
        // Fallback: mostramos la URL para que el admin la copie manual (algunos navegadores
        // bloquean clipboard sin gesto). Toast largo con la URL.
        toast.message("Copia manualmente este enlace:", { description: url, duration: 30000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el enlace de activación");
    } finally {
      setBusyRow(null);
    }
  };

  const cancelarInvitacion = async () => {
    if (!confirmCancel) return;
    const row = confirmCancel;
    setBusyRow(row.id);
    try {
      const { data } = await api.post(`/usuarios/${row.id}/cancelar-invitacion`);
      toast.success(
        `Invitación de ${row.email} cancelada. Tokens invalidados: ${data?.tokens_invalidados ?? 0}. El registro se conserva para auditoría.`
      );
      setConfirmCancel(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar la invitación");
    } finally {
      setBusyRow(null);
    }
  };

  // -------- Eliminar sin huella --------
  // Antes de habilitar el botón "Eliminar de verdad", consultamos la huella operativa.
  // Si hay al menos un rastro, el borrado se rechaza aún si se llegara al POST (guard doble
  // backend+frontend). Al confirmar en el diálogo, se hace el POST real; el backend re-verifica
  // la huella para blindar contra race conditions o payloads manipulados.
  const abrirConfirmEliminar = async (row) => {
    setBusyRow(row.id);
    setConfirmEliminar({ row, huella: null, elegible: null, cargando: true });
    try {
      const { data } = await api.get(`/usuarios/${row.id}/huella-operativa`);
      setConfirmEliminar({
        row,
        huella: data?.huella || [],
        elegible: !!data?.elegible_eliminar,
        total: data?.total || 0,
        cargando: false,
      });
    } catch (e) {
      setConfirmEliminar({ row, huella: [], elegible: false, cargando: false,
                            error: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyRow(null);
    }
  };

  const eliminarSinHuella = async () => {
    if (!confirmEliminar?.row) return;
    const row = confirmEliminar.row;
    setBusyRow(row.id);
    try {
      const { data } = await api.post(`/usuarios/${row.id}/eliminar`);
      toast.success(
        `Usuario ${row.name || row.email} eliminado sin rastro. El correo ${data?.correo_liberado} ya está libre para reutilizarse.`
      );
      setConfirmEliminar(null);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      // Si el backend detectó huella tras el pre-check (race condition), pintamos el detalle.
      if (det && typeof det === "object" && det.huella) {
        setConfirmEliminar({
          row,
          huella: det.huella,
          elegible: false,
          total: det.total_rastros || 0,
          cargando: false,
          error: det.mensaje,
        });
      } else {
        toast.error(typeof det === "string" ? det : "No se pudo eliminar el usuario");
      }
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <div>
      <PageHeader title="Usuarios y roles" subtitle="Gestión de usuarios por área con segregación de funciones">
        {canManage && (
          <Button data-testid="invite-user-btn" onClick={openNew} className="bg-primary hover:bg-sidebar-hover">
            <UserPlus className="mr-1.5 h-4 w-4" /> Invitar usuario
          </Button>
        )}
      </PageHeader>
      <div className="space-y-6 p-6 md:p-8">
        <Tabs defaultValue="usuarios">
          <TabsList>
            <TabsTrigger value="usuarios" data-testid="tab-usuarios-list">Usuarios</TabsTrigger>
            <TabsTrigger value="pendientes" data-testid="tab-usuarios-pendientes">
              Invitaciones pendientes
              {rowsPendientes.length > 0 ? (
                <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                      data-testid="pendientes-badge">
                  {rowsPendientes.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="permisos" data-testid="tab-permisos">Matriz de permisos</TabsTrigger>
          </TabsList>

          <TabsContent value="usuarios" className="mt-5">
            {/* Filtro por estatus — las invitaciones pendientes están en otra tab. */}
            <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
              <Filter className="h-4 w-4 text-slate-400" />
              <span>Filtrar:</span>
              <Select value={filtroEstatus} onValueChange={setFiltroEstatus}>
                <SelectTrigger className="h-8 w-44" data-testid="filtro-estatus">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activos" data-testid="filtro-activos">Sólo activos</SelectItem>
                  <SelectItem value="inactivos" data-testid="filtro-inactivos">Sólo inactivos</SelectItem>
                  <SelectItem value="todos" data-testid="filtro-todos">Todos</SelectItem>
                </SelectContent>
              </Select>
              <span className="ml-auto text-xs text-slate-500" data-testid="filtro-count">
                {rowsUsuariosFiltrados.length} de {rowsUsuarios.length} usuarios
              </span>
            </div>

            <DataTable testid="table-usuarios"
              rows={rowsUsuariosFiltrados.map((r) => ({
                ...r,
                role_label: roleLabel(r.role),
                areas_resumen: areasLabel(r),
                estatus: r.activo === false ? "Inactivo" : "Activo",
              }))}
              columns={[
                { key: "name", label: "Nombre" },
                { key: "email", label: "Correo" },
                { key: "role_label", label: "Rol", kind: "badge" },
                { key: "areas_resumen", label: "Áreas" },
                ...(user?.role === "admin_entidad" ? [
                  { key: "originados", label: "Originados", render: (row) => (
                      <Badge data-testid={`conteo-originados-${row.id}`} variant="outline" className="font-mono">
                        {conteoDe(row.id).originados}
                      </Badge>) },
                  { key: "cartera", label: "En cartera", render: (row) => (
                      <Badge data-testid={`conteo-cartera-${row.id}`} variant="outline" className="font-mono">
                        {conteoDe(row.id).cartera}
                      </Badge>) },
                ] : []),
                { key: "estatus", label: "Estatus", kind: "badge" },
                { key: "created_at", label: "Alta", kind: "date" },
              ]}
              actions={canManage ? (row) => (
                <div className="flex items-center justify-end gap-1">
                  {user?.role === "admin_entidad" && (
                    <TooltipButton tooltip="Ver los clientes asignados a este usuario"
                      data-testid={`manage-clientes-${row.id}`} variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5"
                      onClick={() => setLoteUser(row)}>
                      <Users className="h-4 w-4 text-slate-500" /> Clientes
                    </TooltipButton>
                  )}
                  <TooltipButton tooltip="Cambiar el rol y áreas de este usuario"
                    data-testid={`edit-user-${row.id}`} variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5"
                    onClick={() => openEdit(row)}>
                    <Pencil className="h-4 w-4 text-slate-500" /> Editar
                  </TooltipButton>
                  {row.activo === false ? (
                    <TooltipButton
                      tooltip="Restaura el acceso del usuario. Conserva su rol y áreas."
                      data-testid={`reactivate-user-${row.id}`} variant="ghost" size="sm"
                      className="h-8 gap-1.5 px-2.5 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                      disabled={busyRow === row.id || row.id === user?.id}
                      onClick={() => reactivar(row)}>
                      {busyRow === row.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <UserCheck className="h-4 w-4" />}
                      Reactivar
                    </TooltipButton>
                  ) : (
                    <TooltipButton
                      tooltip="El usuario pierde acceso de inmediato. Sus registros se conservan (Disp. 43ª)."
                      data-testid={`deactivate-user-${row.id}`} variant="ghost" size="sm"
                      className="h-8 gap-1.5 px-2.5 text-red-700 hover:bg-red-50 hover:text-red-800"
                      disabled={busyRow === row.id || row.id === user?.id}
                      onClick={() => abrirConfirmDesactivar(row)}>
                      {busyRow === row.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <UserX className="h-4 w-4" />}
                      Desactivar
                    </TooltipButton>
                  )}
                  <TooltipButton
                    tooltip="Elimina físicamente el registro. Sólo permitido si el usuario NO tiene rastros de auditoría (nunca operó, firmó, ni aprobó nada). Libera el correo para reutilizarse."
                    data-testid={`delete-user-${row.id}`} variant="ghost" size="sm"
                    className="h-8 gap-1.5 px-2.5 text-red-800 hover:bg-red-100 hover:text-red-900"
                    disabled={busyRow === row.id || row.id === user?.id}
                    onClick={() => abrirConfirmEliminar(row)}>
                    {busyRow === row.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />}
                    Eliminar
                  </TooltipButton>
                </div>
              ) : undefined}
              emptyLabel={
                filtroEstatus === "activos" ? "No hay usuarios activos"
                : filtroEstatus === "inactivos" ? "No hay usuarios desactivados"
                : "Sin usuarios"
              } />
          </TabsContent>

          <TabsContent value="pendientes" className="mt-5">
            {/* Justificación de tab separada:
                un usuario invitado que nunca aceptó el correo NO es un usuario dado de baja;
                nunca operó, nunca tuvo cartera, nunca firmó nada. Mezclarlo en el listado
                principal como "Inactivo" (que era el estado previo) engañaba al admin y
                confundía con desactivaciones deliberadas. En su propia tab, con contador
                de pendientes visible en el TabsTrigger y sólo dos acciones (Reenviar /
                Cancelar), el ciclo de invitación queda separado del ciclo de usuarios reales.
            */}
            <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                 data-testid="pendientes-help">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span>
                <b>Invitaciones enviadas que nunca fueron aceptadas.</b> Estos registros aún no son
                usuarios operativos (jamás iniciaron sesión ni firmaron nada). Puedes <b>reenviar</b>
                el correo con un nuevo enlace o <b>cancelar</b> la invitación. Cancelar es baja lógica:
                no borra el registro, sólo invalida los tokens y lo saca del listado — obligatorio
                para mantener la traza Disp. 43ª.
              </span>
            </div>
            <DataTable testid="table-pendientes"
              rows={rowsPendientes.map((r) => ({
                ...r,
                role_label: roleLabel(r.role),
                areas_resumen: areasLabel(r),
                estatus_pendiente: r.invitacion_cancelada_at ? "Cancelada"
                                   : (r.invitacion_reenviada_at ? "Reenviada" : "Enviada"),
                reenvios: r.invitacion_reenvios || 0,
              }))}
              columns={[
                { key: "name", label: "Nombre" },
                { key: "email", label: "Correo" },
                { key: "role_label", label: "Rol", kind: "badge" },
                { key: "areas_resumen", label: "Áreas" },
                { key: "estatus_pendiente", label: "Invitación", kind: "badge" },
                { key: "reenvios", label: "Reenvíos", render: (row) => (
                    <Badge data-testid={`reenvios-${row.id}`} variant="outline" className="font-mono">
                      {row.invitacion_reenvios || 0}
                    </Badge>) },
                { key: "created_at", label: "Invitado", kind: "date" },
              ]}
              actions={canManage ? (row) => (
                <div className="flex items-center justify-end gap-1">
                  <TooltipButton
                    tooltip="Vuelve a enviar el correo de invitación con un nuevo enlace válido."
                    data-testid={`resend-invite-${row.id}`} variant="ghost" size="sm"
                    className="h-8 gap-1.5 px-2.5 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                    disabled={busyRow === row.id || row.activo === false}
                    onClick={() => reenviarInvitacion(row)}>
                    {busyRow === row.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Send className="h-4 w-4" />}
                    Reenviar
                  </TooltipButton>
                  <TooltipButton
                    tooltip="Genera un enlace de activación y lo copia al portapapeles. Útil cuando el correo no llega — puedes compartirlo por chat o WhatsApp."
                    data-testid={`copy-link-invite-${row.id}`} variant="ghost" size="sm"
                    className="h-8 gap-1.5 px-2.5 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    disabled={busyRow === row.id || row.activo === false}
                    onClick={() => copiarLinkInvitacion(row)}>
                    {busyRow === row.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <LinkIcon className="h-4 w-4" />}
                    Copiar enlace
                  </TooltipButton>
                  <TooltipButton
                    tooltip="Cancela la invitación sin enviar más correos. Baja lógica; el registro se conserva."
                    data-testid={`cancel-invite-${row.id}`} variant="ghost" size="sm"
                    className="h-8 gap-1.5 px-2.5 text-red-700 hover:bg-red-50 hover:text-red-800"
                    disabled={busyRow === row.id || row.activo === false}
                    onClick={() => setConfirmCancel(row)}>
                    {busyRow === row.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <MailX className="h-4 w-4" />}
                    Cancelar
                  </TooltipButton>
                  <TooltipButton
                    tooltip="Elimina físicamente el registro. Ideal para invitaciones con correo mal escrito — libera el correo para volver a invitarlo bien."
                    data-testid={`delete-invite-${row.id}`} variant="ghost" size="sm"
                    className="h-8 gap-1.5 px-2.5 text-red-800 hover:bg-red-100 hover:text-red-900"
                    disabled={busyRow === row.id}
                    onClick={() => abrirConfirmEliminar(row)}>
                    {busyRow === row.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />}
                    Eliminar
                  </TooltipButton>
                </div>
              ) : undefined}
              emptyLabel="No hay invitaciones pendientes" />
          </TabsContent>

          <TabsContent value="permisos" className="mt-5">
            <MatrizPermisos />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar usuario" : "Invitar usuario"}</DialogTitle>
            <DialogDescription className="sr-only">Datos del usuario, rol y áreas de acceso del menú</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="mb-1 block text-xs">Nombre completo</Label><Input data-testid="inv-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div><Label className="mb-1 block text-xs">Correo</Label>
              <Input data-testid="inv-email" type="email" value={form.email}
                     onChange={(e) => set("email", e.target.value)} />
              {editing && form.email !== editing.email && (
                <p className="mt-1 text-[11px] text-amber-700" data-testid="email-change-warning">
                  ⚠️ Cambiar el correo requiere una nueva verificación por parte del usuario. Su sesión activa se conserva (el token no depende del correo), pero al re-iniciar sesión usará el correo nuevo. Se enviará un correo de verificación a la dirección nueva automáticamente.
                </p>
              )}
            </div>
            <div><Label className="mb-1 block text-xs">Teléfono</Label><Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} /></div>
            <div>
              <Label className="mb-1 block text-xs">Rol</Label>
              <Select value={form.role} onValueChange={onRole}>
                <SelectTrigger data-testid="inv-role"><SelectValue placeholder="Selecciona un rol…" /></SelectTrigger>
                <SelectContent>{roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}{r.en_uso ? " · (rol heredado, en uso)" : ""}
                  </SelectItem>
                ))}</SelectContent>
              </Select>
              {esAV && (
                <p className="mt-1 text-[11px] text-slate-500" data-testid="inv-role-av-note">
                  <b>Segregación de funciones</b> (LFPIORPI): en actividades vulnerables solo hay
                  dos roles. El <b>Administrador de Entidad</b> captura y opera. El{" "}
                  <b>Oficial de Cumplimiento</b> revisa, dictamina y NO captura operaciones.
                  Nunca deben ser la misma persona.
                </p>
              )}
            </div>
            <div>
              <Label className="mb-1 block text-xs">Área (organigrama)</Label>
              <Select value={form.area} onValueChange={(v) => set("area", v)}>
                <SelectTrigger data-testid="inv-area-hr"><SelectValue /></SelectTrigger>
                <SelectContent>{areasHr.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="mb-1 block text-xs">Puesto</Label><Input value={form.puesto} onChange={(e) => set("puesto", e.target.value)} /></div>
          </div>

          {!isPlatform && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="areas-acceso-box">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">Áreas de acceso (menú)</p>
              <p className="mb-2 text-[11px] text-slate-500">
                El usuario solo verá y operará las áreas marcadas. "Principal" siempre está disponible.
                Roles de administrador de la entidad tienen acceso total automático.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {areasVisibles.map((a) => (
                  <label key={a.id} data-testid={`area-row-${a.id}`}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm">
                    <Checkbox
                      data-testid={`area-check-${a.id}`}
                      checked={areaChecked(a.id)}
                      disabled={a.id === "principal"}
                      onCheckedChange={() => toggleArea(a.id)}
                    />
                    <span className="text-slate-700">{a.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {isPlatform && (
            <div data-testid="aviso-rol-plataforma"
              className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-800">
              <b>Usuario de plataforma FELVORA</b> (sin entidad). El «Cargador de Listas»
              solo accede al <b>Centro de Listas PLD</b> y al <b>Barrido diario
              centralizado</b>: sus áreas las fija la plataforma y su segundo factor es
              obligatorio. Ninguna otra pantalla, módulo ni dato de las entidades queda a
              su alcance.
            </div>
          )}

          {editing && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5" data-testid="activo-box">
              <div>
                <p className="text-sm font-medium text-slate-700">Usuario activo</p>
                <p className="text-[11px] text-slate-500">Al desactivar, el usuario no podrá iniciar sesión. Sus registros se conservan (baja lógica).</p>
              </div>
              <Switch data-testid="user-activo-switch" checked={form.activo} onCheckedChange={(v) => set("activo", v)} />
            </div>
          )}

          {editing && user?.role === "admin_entidad" && totalClientesDe(editing.id) > 0 && (
            <div data-testid="desactivar-aviso-clientes"
              className={`rounded-lg border px-3 py-2.5 ${form.activo ? "border-amber-200 bg-amber-50/60" : "border-red-200 bg-red-50/70"}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${form.activo ? "text-amber-500" : "text-red-500"}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">
                    Este usuario tiene {totalClientesDe(editing.id)} cliente(s) asignado(s)
                    <span className="font-normal text-slate-500"> ({conteoDe(editing.id).originados} originados · {conteoDe(editing.id).cartera} en cartera)</span>
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {form.activo
                      ? "Si lo desactivas, sus clientes quedarán sin responsable activo. Reasígnalos a otra persona primero."
                      : "Al desactivar quedarán sin responsable activo. Te recomendamos reasignarlos antes de guardar."}
                  </p>
                  <Button data-testid="desactivar-reasignar-btn" size="sm" variant="outline" className="mt-2"
                    onClick={() => { setOpen(false); setLoteUser(editing); }}>
                    <Users className="mr-1.5 h-4 w-4" /> Reasignar todos a…
                  </Button>
                </div>
              </div>
            </div>
          )}

          {editing && !isPlatform && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="cartera-box">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">Cartera de clientes asignada</p>
              <p className="mb-2 text-[11px] text-slate-500">
                Asigna clientes a este usuario como responsable de cartera. Cobranza ve únicamente estos clientes;
                un Ejecutivo de Crédito ve estos más los que él mismo capturó. Si marcas un cliente que ya está
                asignado a otra persona, al guardar se le quita a esa persona.
              </p>
              <Input data-testid="cartera-search" placeholder="Buscar cliente por nombre o RFC..."
                value={carteraSearch} onChange={(e) => setCarteraSearch(e.target.value)} className="mb-2" />
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                {carteraFiltrada.length === 0 && <p className="px-1 py-2 text-xs text-slate-400">Sin clientes para mostrar.</p>}
                {carteraFiltrada.map((c) => (
                  <label key={c.id} data-testid={`cartera-row-${c.id}`}
                    className="flex items-center gap-2 rounded px-1.5 py-1.5 text-sm hover:bg-slate-50">
                    <Checkbox data-testid={`cartera-check-${c.id}`} checked={carteraSel.has(c.id)}
                      onCheckedChange={() => toggleCartera(c.id)} />
                    <span className="text-slate-700">{c.nombre || c.razon_social}</span>
                    <span data-testid={`cartera-credito-${c.id}`}
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${conCredito.has(c.id) ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {conCredito.has(c.id) ? "Con crédito" : "Sin crédito"}
                    </span>
                    <span className="ml-auto text-[11px] text-slate-400" data-testid={`cartera-asignado-${c.id}`}>
                      {c.rfc ? `${c.rfc} · ` : ""}
                      {c.cobrador_id
                        ? (c.cobrador_id === editing?.id ? "Asignado a este usuario" : `Asignado a: ${c.cobrador_nombre || "—"}`)
                        : "Sin asignar"}
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">{carteraSel.size} cliente(s) seleccionado(s)</span>
                <Button data-testid="save-cartera-btn" size="sm" variant="outline" onClick={saveCartera} disabled={carteraSaving}>
                  {carteraSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Guardar cartera
                </Button>
              </div>
            </div>
          )}

          {!editing && <p className="text-xs text-slate-400">El usuario recibirá un correo con un enlace para activar su cuenta y definir su propia contraseña. {isPlatform ? "Quedará como usuario de la plataforma, sin entidad asignada." : "Quedará ligado a tu entidad."}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="save-invite-btn" onClick={save} disabled={saving || !form.role} className="bg-primary hover:bg-sidebar-hover">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {editing ? "Guardar cambios" : "Invitar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReasignarLoteUsuario
        targetUser={loteUser}
        open={!!loteUser}
        onOpenChange={(v) => { if (!v) setLoteUser(null); }}
        onDone={load}
      />

      {/* Diálogo de confirmación de desactivar — aviso explícito sobre cartera asignada.
          La cartera NO se reasigna aquí (esa es otra decisión del admin). Sólo advertimos:
          si el admin reasigna manualmente la cartera con este usuario ya desactivado, al
          reactivarlo NO se recupera automáticamente. Es intencionado: la reasignación es
          un acto propio del nuevo cobrador y no debe deshacerse sin su intervención. */}
      <Dialog open={!!confirmDesactivar} onOpenChange={(v) => { if (!v) setConfirmDesactivar(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-desactivar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-red-800">
              <UserX className="h-5 w-5" /> Desactivar usuario
            </DialogTitle>
            <DialogDescription>
              Estás por dar de baja lógica a <b>{confirmDesactivar?.row?.name}</b>
              {" "}(<span className="font-mono">{confirmDesactivar?.row?.email}</span>).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <ul className="list-disc space-y-1 pl-5">
              <li>El usuario <b>pierde acceso de inmediato</b> — su sesión se cierra en el siguiente request.</li>
              <li>Sus registros históricos <b>se conservan íntegros</b>: aprobaciones, desbloqueos, overrides, pagos, documentos y bitácora quedan ligados a su id para la traza Disp. 43ª (mínimo 5 años).</li>
              <li>Puedes reactivarlo cuando quieras. Recupera su rol y sus áreas.</li>
            </ul>
            {confirmDesactivar?.cartera && confirmDesactivar.cartera.total > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                   data-testid="confirm-desactivar-cartera-aviso">
                <div className="mb-1 flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="h-4 w-4 text-amber-700" /> Este usuario tiene cartera asignada
                </div>
                <div className="text-amber-900">
                  <b data-testid="confirm-cartera-cobrador">{confirmDesactivar.cartera.cobrador_id_count}</b> clientes en cartera
                  {" · "}
                  <b data-testid="confirm-cartera-originados">{confirmDesactivar.cartera.originado_por_id_count}</b> clientes originados
                  {" (total "}<b>{confirmDesactivar.cartera.total}</b>{")."}
                </div>
                <div className="mt-1 text-amber-800">
                  Al desactivar, sus clientes <b>no cambian de dueño automáticamente</b> — siguen apuntando a este usuario.
                  Si necesitas moverlos, hazlo <i>antes o después</i> con “Clientes → Reasignar”. Si reasignas después,
                  al reactivar este usuario <b>no recupera</b> los clientes movidos (comportamiento intencionado: la
                  reasignación es un acto propio del nuevo responsable).
                </div>
              </div>
            )}
            {confirmDesactivar?.cartera && confirmDesactivar.cartera.total === 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                   data-testid="confirm-desactivar-cartera-sin">
                Sin cartera asignada: la desactivación no afecta clientes de la entidad.
              </div>
            )}
            {confirmDesactivar?.cartera_error && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500"
                   data-testid="confirm-desactivar-cartera-error">
                No se pudo consultar la cartera: {confirmDesactivar.cartera_error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDesactivar(null)}
                    data-testid="confirm-desactivar-cancelar">
              Cancelar
            </Button>
            <Button className="bg-red-600 text-white hover:bg-red-700"
                    disabled={busyRow === confirmDesactivar?.row?.id}
                    onClick={desactivar}
                    data-testid="confirm-desactivar-confirmar">
              {busyRow === confirmDesactivar?.row?.id
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <UserX className="mr-2 h-4 w-4" />}
              Confirmar desactivación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación de cancelar invitación — baja lógica con aviso claro. */}
      <Dialog open={!!confirmCancel} onOpenChange={(v) => { if (!v) setConfirmCancel(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-red-800">
              <MailX className="h-5 w-5" /> Cancelar invitación
            </DialogTitle>
            <DialogDescription>
              La invitación enviada a <b>{confirmCancel?.email}</b> quedará invalidada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-700">
            <ul className="list-disc space-y-1 pl-5">
              <li>Los <b>tokens del correo original dejan de funcionar</b>: si el invitado hace clic en un enlace antiguo, no podrá activar la cuenta.</li>
              <li>El registro <b>se conserva</b> en la base de datos (baja lógica) — sale del listado, pero mantiene su historial para auditoría.</li>
              <li>Si más adelante quieres volver a invitar a esa persona, crea una <b>nueva invitación</b> desde “Invitar usuario”.</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(null)}
                    data-testid="confirm-cancel-invitacion-cancelar">
              Volver
            </Button>
            <Button className="bg-red-600 text-white hover:bg-red-700"
                    disabled={busyRow === confirmCancel?.id}
                    onClick={cancelarInvitacion}
                    data-testid="confirm-cancel-invitacion-confirmar">
              {busyRow === confirmCancel?.id
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <MailX className="mr-2 h-4 w-4" />}
              Cancelar invitación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de eliminación física condicional.
          Muestra el resultado del GET /huella-operativa:
          - Si `elegible=true` (huella vacía): botón rojo Confirmar habilitado.
          - Si `elegible=false`: listado enumerado de rastros con conteo y colección,
            botón deshabilitado con explicación del porqué. */}
      <Dialog open={!!confirmEliminar} onOpenChange={(v) => { if (!v) setConfirmEliminar(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="confirm-eliminar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-red-800">
              <Trash2 className="h-5 w-5" /> Eliminar usuario sin huella
            </DialogTitle>
            <DialogDescription>
              Borrado físico del registro <b>{confirmEliminar?.row?.name || confirmEliminar?.row?.email}</b>
              {" "}(<span className="font-mono">{confirmEliminar?.row?.email}</span>).
            </DialogDescription>
          </DialogHeader>
          {confirmEliminar?.cargando ? (
            <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="eliminar-cargando">
              <Loader2 className="h-4 w-4 animate-spin" /> Verificando rastros operativos…
            </div>
          ) : confirmEliminar?.elegible ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800"
                   data-testid="eliminar-sin-huella-ok">
                <b>Elegible.</b> Este usuario NO tiene rastros operativos — jamás ejecutó ningún acto que deba preservarse por auditoría. Su registro puede eliminarse físicamente sin dañar la traza Disp. 43ª.
              </div>
              <ul className="list-disc space-y-1 pl-5 text-slate-700">
                <li>El documento en <code>users</code> se borra por completo.</li>
                <li>El correo <b>{confirmEliminar?.row?.email}</b> queda libre para volver a asignarse (por ejemplo, si estaba mal escrito y quieres re-invitar bien).</li>
                <li>Se registra un evento <code>eliminar_sin_huella</code> en bitácora con nombre, correo, rol y fecha del usuario borrado — la traza administrativa se conserva aunque el documento fuente ya no exista.</li>
              </ul>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-900"
                   data-testid="eliminar-con-huella-rechazo">
                <div className="flex items-center gap-1.5 font-semibold">
                  <ShieldAlert className="h-4 w-4" />
                  No se puede eliminar — el usuario tiene actividad operativa
                </div>
                <p className="mt-1 text-xs">
                  Encontramos <b>{confirmEliminar?.total}</b> rastro(s) que deben conservarse por auditoría regulatoria (Disp. 43ª CNBV / Art. 115 LIC, mínimo 5 años).
                  Usa <b>Desactivar</b> en su lugar — el usuario pierde acceso y su historial queda íntegro.
                </p>
              </div>
              {confirmEliminar?.huella?.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="eliminar-huella-detalle">
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Rastros encontrados:</p>
                  <ul className="space-y-1 text-xs text-slate-700">
                    {confirmEliminar.huella.map((h, i) => (
                      <li key={i} className="flex items-start gap-2" data-testid={`huella-item-${i}`}>
                        <Badge variant="outline" className="font-mono text-[10px]">{h.count}</Badge>
                        <span>{h.label}
                          <span className="ml-1 text-slate-400">
                            ({h.coleccion}.{h.campo})
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {confirmEliminar?.error && (
                <p className="text-xs text-slate-500" data-testid="eliminar-error-detalle">{confirmEliminar.error}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmEliminar(null)}
                    data-testid="confirm-eliminar-cancelar">
              Volver
            </Button>
            <Button className="bg-red-700 text-white hover:bg-red-800 disabled:opacity-60"
                    disabled={!confirmEliminar?.elegible || busyRow === confirmEliminar?.row?.id}
                    onClick={eliminarSinHuella}
                    data-testid="confirm-eliminar-confirmar">
              {busyRow === confirmEliminar?.row?.id
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Trash2 className="mr-2 h-4 w-4" />}
              Confirmar eliminación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
